import { useState } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Grid, IconButtonGhostMini, Tooltip, Typography } from '@kc-console/common-ui';
import { useAddFavoriteService, useDeleteFavoriteService } from '@kc-console/context';
import { useTranslation } from '@kc-console/core';
import { SpriteImgIcon } from '@kc-console/shared-ui';
import { IcArrowEnterRegular } from '@kep/kcds-icons';
import IcFavoriteFill from '@kep/kcds-icons/IcFavoriteFill';
import IcFavoriteRegular from '@kep/kcds-icons/IcFavoriteRegular';

import { MAX_FAVORITE_SERVICE_COUNT, useRefinedFavoriteServiceState } from '../../../../hooks';
import ServiceBadgeList from './ServiceBadgeList';

import type { MouseEvent } from 'react';
import type { MegaMenuService } from '@kc-console/context';

interface Props {
  open: boolean;
  service: MegaMenuService & { isFavorite?: boolean };
}

const CategoryMenuItem = ({ service, open }: Props) => {
  const { t } = useTranslation();
  const { name, keyword, description, isFavorite } = service;

  const { favoriteServices = [] } = useRefinedFavoriteServiceState();

  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipMessage, setTooltipMessage] = useState('');

  const { mutate: addFavoriteService } = useAddFavoriteService();
  const { mutate: deleteFavoriteService } = useDeleteFavoriteService();

  const handleClickFavorite = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isFavorite) {
      deleteFavoriteService(service);
      setTooltipMessage(t('layout:favorite.toast.removed'));
    } else if (favoriteServices.length >= MAX_FAVORITE_SERVICE_COUNT) {
      setTooltipMessage(
        t('layout:favorite.toast.max_limit', { maxCount: MAX_FAVORITE_SERVICE_COUNT }),
      );
    } else {
      addFavoriteService(service);
      setTooltipMessage(t('layout:favorite.toast.added'));
    }

    setShowTooltip(true);
  };

  return (
    <MenuItemContainer>
      <MenuItemLeftContainer>
        <SpriteImgIcon fontSize={24} id={keyword} category={'BeyondServiceRegular'} />
        <StyledServiceInfo>
          <Grid container alignItems={'center'} spacing={0.5}>
            <ServiceName
              variant={'label/medium/md'}
              color={open ? 'text/informative/default' : 'text/default'}
            >
              {name}
            </ServiceName>
            <ServiceBadgeList badges={service.badges} />

            {(open || isFavorite) && (
              <Tooltip message={showTooltip ? tooltipMessage : ''} placement={'top'} arrow>
                <FavoriteIconButton size="md" onClick={handleClickFavorite}>
                  {isFavorite ? <IcFavoriteFill /> : <IcFavoriteRegular />}
                </FavoriteIconButton>
              </Tooltip>
            )}
          </Grid>
          <ServiceDescription variant={'body/normal/xs'}>{description}</ServiceDescription>
        </StyledServiceInfo>
      </MenuItemLeftContainer>
      <div id="enter-icon">
        <IcArrowEnterRegular fontSize={16} />
      </div>
    </MenuItemContainer>
  );
};

const MenuItemContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding: 6px 24px 6px 12px;

  #enter-icon {
    display: none;
  }

  &:hover,
  &:active {
    #enter-icon {
      display: block;
    }
  }
`;

const MenuItemLeftContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledServiceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-left: 12px;
`;

const ServiceName = styled(Typography)`
  font-weight: 500;
  line-height: 24px;
`;
const ServiceDescription = styled(Typography)`
  font-size: 12px;
  color: #676f7d;
  line-height: 18px;
  max-width: 596px;
`;

const FavoriteIconButton = styled(IconButtonGhostMini)(
  ({ theme }) => css`
    margin-left: 4px;
    font-size: 12px;
    color: ${theme.color['icon/default']};
  `,
);

export default CategoryMenuItem;
